import React, { FC, ReactNode, useState, useRef, useEffect, createContext, useContext, MouseEvent, HTMLAttributes, useMemo } from "react";

import classnames from "classnames";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import Icon from "Common/Icon";
import { SwapVerticalArrowsIconComponent } from "Common/Icon/Custom/SwapVerticalArrowsIcon";

import Button, { ButtonProps } from "./Button";

/** Detect clicks outside one or more elements (updated to support portaled version) */
export function useOnClickOutside(
  refs: React.RefObject<HTMLElement> | React.RefObject<HTMLElement>[],
  handler: (event: MouseEvent | TouchEvent) => void,
  enabled = true,
) {
  const refArray = useMemo(() => (Array.isArray(refs) ? refs : [refs]), [refs]);

  useEffect(() => {
    if (!enabled) return;

    const listener = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && refArray.some((r) => r.current && r.current.contains(target))) {
        return;
      }
      handler(event);
    };

    document.addEventListener("mousedown", listener as any, true);
    document.addEventListener("touchstart", listener as any, true);
    return () => {
      document.removeEventListener("mousedown", listener as any, true);
      document.removeEventListener("touchstart", listener as any, true);
    };
  }, [enabled, handler, refArray]);
}

// Context to provide onSelect and closeDropdown to Options
export interface DropdownContextProps {
  onSelect?: (value: any) => void;
  closeDropdown: () => void;
  keepOpenOnSelect: boolean;
}

const DropdownContext = createContext<DropdownContextProps>({
  closeDropdown: () => {},
  keepOpenOnSelect: false,
});

export interface OptionProps {
  /** If you need to link, use this "to" prop, this will avoid having <a> tag inside another <a> tag * */
  to?: string;
  value?: any;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  onSelect?: (value: any) => void;
  severity?: "danger" | "info" | "success" | "warning" | string;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
}

export const Option: FC<OptionProps> = ({ to, value, disabled, onClick, onSelect, severity, children, className }) => {
  const { onSelect: contextOnSelect, closeDropdown, keepOpenOnSelect } = useContext(DropdownContext);

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    if (disabled) {
      e.preventDefault();
      return;
    }

    if (onClick) {
      onClick(e);
    }
    if (onSelect) {
      onSelect(value);
    }
    if (contextOnSelect) {
      contextOnSelect(value);
    }
    if (!keepOpenOnSelect) {
      closeDropdown();
    }

    if (!to) {
      e.preventDefault();
    }
  };

  const severityClasses = {
    default: "text-inherit hover:text-inherit",
    danger: "text-error-900 text-bold hover:text-error-900 hover:bg-error-50",
    info: "text-info-900 hover:text-info-900 hover:bg-info-50",
    success: "text-success-900 hover:text-success-900 hover:bg-success-50",
    warning: "text-warning-900 hover:text-warning-900 hover:bg-warning-50",
    disabled: "text-muted hover:text-muted cursor-not-allowed",
  };

  const classes = classnames(
    "si-menu-item text-body2 whitespace-nowrap hover:no-underline",
    severityClasses[severity || "default"],
    { [severityClasses.disabled]: disabled },
    twMerge("group px-4 py-2", className),
  );

  const content = to ? (
    <Link to={to} role="menuitem" className={classes} onClick={handleClick}>
      {children}
    </Link>
  ) : (
    /**
     * Normally we would use a button, however, a menuitem has better cross-browser support
     * because Safari has issues with buttons within menus
     * @see https://bugs.webkit.org/show_bug.cgi?id=22261
     */
    /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
    <a role="menuitem" href="#" className={classes} onClick={handleClick}>
      {children}
    </a>
  );

  return (
    <li role="presentation" className="w-full">
      {content}
    </li>
  );
};

export interface DropdownProps {
  id?: string;
  pullRight?: boolean;
  toggle: ReactNode;
  children?: ReactNode;
  onSelect?: (value: any) => void;
  className?: string;
  menuClassName?: string;
  keepOpenOnSelect?: boolean;
  /** When true, render the menu in a portal to avoid "overflow: hidden" issues */
  usePortal?: boolean;
}

export const Dropdown: FC<DropdownProps> = ({
  id = "cobalt-dropdown",
  pullRight = false,
  toggle,
  children,
  onSelect,
  className,
  menuClassName,
  keepOpenOnSelect = false,
  usePortal = false,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});

  useOnClickOutside([containerRef, menuRef], () => setOpen(false), open);

  const handleToggleClick = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };

  const positionMenu = () => {
    if (!usePortal) return;
    const el = containerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();

    const baseLeft = pullRight ? rect.right : rect.left;

    const style: React.CSSProperties = {
      position: "fixed",
      top: rect.bottom,
      left: baseLeft,
      minWidth: rect.width,
      zIndex: "var(--zindex-dropdown)" as unknown as number,
      transform: pullRight ? "translateX(-100%)" : "none",
    };

    setPortalStyle(style);
  };

  useEffect(() => {
    if (!open || !usePortal) return () => {};

    positionMenu();

    const onScroll = () => positionMenu();
    const onResize = () => positionMenu();

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, usePortal, pullRight]);

  const dropdownClasses = twMerge(
    "si-dropdown relative",
    {
      "si-dropdown-end": pullRight,
      "si-dropdown-open": open,
    },
    className,
  );

  const contextValue = {
    onSelect,
    keepOpenOnSelect,
    closeDropdown: () => setOpen(false),
  };

  const renderToggle = () => {
    if (typeof toggle === "string") {
      return (
        <Button color="ghost" className="p-0" onClick={handleToggleClick}>
          {toggle}
        </Button>
      );
    }
    return React.cloneElement(toggle as React.ReactElement<any>, {
      onClick: handleToggleClick,
    });
  };

  const baseMenuClasses =
    "si-dropdown-content si-menu text-sm bg-primary-content border border-solid border-neutral-100 text-left shadow-sm rounded-box p-2 mb-0 min-w-[180px] z-dropdown";

  const nonPortalMenuClasses = classnames(
    baseMenuClasses,
    "absolute top-full",
    {
      hidden: !open,
      "right-0": pullRight,
      "left-0": !pullRight,
    },
    menuClassName,
  );

  const portalMenu = (
    <DropdownContext.Provider value={contextValue}>
      <ul
        ref={menuRef}
        role="menu"
        // In portal mode the UL is fixed-positioned via style
        className={twMerge(baseMenuClasses, menuClassName)}
        style={portalStyle}
      >
        {children}
      </ul>
    </DropdownContext.Provider>
  );

  return (
    <div id={id} className={dropdownClasses} ref={containerRef}>
      {renderToggle()}

      {!usePortal && (
        <DropdownContext.Provider value={contextValue}>
          <ul role="menu" ref={menuRef} className={nonPortalMenuClasses}>
            {children}
          </ul>
        </DropdownContext.Provider>
      )}

      {usePortal && open && typeof document !== "undefined" ? createPortal(portalMenu, document.body) : null}
    </div>
  );
};

/** Render a three-button menu ghost button */
const MenuToggleButton: FC<ButtonProps> = ({ className, ...props }) => (
  <Button color="ghost" {...props} className={twMerge("rounded w-10 h-10", className)}>
    <Icon icon="ellipsis-v" size="lg" />
  </Button>
);

export const MenuDropdown: FC<DropdownProps> = ({
  pullRight = false,
  toggle = <MenuToggleButton />,
  children,
  onSelect,
  className,
  menuClassName,
  ...rest
}) => {
  const combinedClassName = twMerge(classnames(className));
  return (
    <Dropdown toggle={toggle} className={combinedClassName} pullRight={pullRight} onSelect={onSelect} menuClassName={menuClassName} {...rest}>
      {children}
    </Dropdown>
  );
};

// DropdownButtonProps interface
export interface DropdownButtonProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  disabled?: boolean;
  color?: ButtonProps["color"];
  round?: ButtonProps["round"];
  size?: "small" | "xs";
  type?: string;
  className?: string;
  buttonClassName?: string;
  dropdownClassName?: string;
  dropdownPosition?: "left" | "right";
  keepOpenOnSelect?: boolean;
  sort?: boolean;
  onSelect?: (value: any) => void;
  /**
   * Renders the dropdown menu in a portal at the root of the document
   * This is useful for avoiding clipping issues in collapsible containers (which use overflow: hidden)
   * Note: This method relies on JavaScript for positioning, which may have edge cases compared to standard CSS layout. Use only when necessary
   */
  usePortal?: boolean;
}

export const DropdownButton: FC<DropdownButtonProps> = ({
  title,
  disabled,
  size = "small",
  round,
  color = "tertiary",
  className,
  buttonClassName,
  dropdownClassName,
  dropdownPosition = "left",
  keepOpenOnSelect = false,
  sort = false,
  children,
  onSelect,
  usePortal = false,
  ...rest
}) => {
  const buttonClasses = twMerge(buttonClassName, className);

  const toggleButton = (
    <Button color={color} size={size} round={round} disabled={disabled} className={buttonClasses}>
      {title}
      {sort ? <SwapVerticalArrowsIconComponent /> : <Icon icon="caret-down" flavor="solid" spacing="left" />}
    </Button>
  );

  return (
    <Dropdown
      {...rest}
      toggle={toggleButton}
      pullRight={dropdownPosition === "right"}
      onSelect={onSelect}
      keepOpenOnSelect={keepOpenOnSelect}
      menuClassName={dropdownClassName}
      usePortal={usePortal}
    >
      {children}
    </Dropdown>
  );
};
